import { Translator } from 'i18n';
import { athletesByActivity } from './__mocks__/mockData';

/**
 * List of possible errors returned by backend when baking an activity:
 *
 * error:invalid
 * error:failed
 * error:no activity user
 * error:invalid activity
 * error:sync up efforts fail
 * error:sync up bandstats fail
 * error:sync up chartdata package fail
 * error:sync up padded sensor data fail
 * error:sync up chartdata fail
 * error:no bake results
 * error:no data found
 * error:no files synced
 * error:no PIP
 */

// derived types, for frontend use (tags - variants)
export type ActivityStatusVariant = 'processing' | 'processed' | 'error';
export type EveryActivityStatusVariant =
    | ActivityStatusVariant
    | `processedWithMissing-${number}-${number}` // numbers: notMunged, total(in activity in a period)
    | `noFilesSyncedError`
    | `noPipError`;

export const isActivityProcessing = (status: string): boolean => {
    return (
        status.includes('prepping') ||
        status.includes('baking') ||
        status.includes('loading') ||
        status.includes('queued') ||
        status.includes('debounce_wait')
    );
};

export type AthleteProcessingStatus = 'Pending' | 'InProgress' | 'Completed' | 'Failed';

export type AthleteStatusDataSource = 'Live' | 'Munged' | 'Unknown';

// type for object returned from endpoint
export type ActivityStatusResponse = {
    activityId: string;
    status: string;
    modifiedDate: string;
};

export type PagedActivityBakeStatusResponse = {
    items: ActivityStatusResponse[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages?: number; // not in OpenAPI required list — may be absent
};

// type for object returned from endpoint
export type AthleteStatusResponse = {
    activityId: string;
    athleteId: string;
    status: AthleteProcessingStatus;
    dataSource: AthleteStatusDataSource;
    modifiedDate: string;
};

export function generateMockActivityStatus(): string {
    const status_options: string[] = [
        'baking',
        'baked',
        'baked 7-4-11',
        'baked 2-9-11',
        'baked 0-11-11',
        'error:something',
        'error:no PIP',
        'error:no files synced',
    ];
    return status_options[Math.floor(Math.random() * status_options.length)];
}

export function generateMockAthleteStatuses(activityId: string): AthleteStatusResponse[] {
    const status_options: AthleteProcessingStatus[] = ['Pending', 'InProgress', 'Completed', 'Failed'];
    const data_source_options: AthleteStatusDataSource[] = ['Live', 'Munged'];

    // using real activity athletes to generate the mock statuses
    const athleteIds = (): string[] => {
        const match = athletesByActivity.find((entry) => activityId in entry);
        if (!match) {
            return [];
        }

        type MatchKey = keyof typeof match;
        const activityIdAsKey = activityId as MatchKey;
        return match[activityIdAsKey]?.map((athlete) => athlete.id) ?? [];
    };

    return athleteIds().map((athleteId): AthleteStatusResponse => {
        return {
            activityId,
            athleteId,
            status: status_options[Math.floor(Math.random() * status_options.length)],
            dataSource: data_source_options[Math.floor(Math.random() * data_source_options.length)],
            modifiedDate: new Date().toISOString(),
        };
    });
}

// upserts freshItems into accumulated (by activityId, keeping the newer modifiedDate on conflict) without mutating
// the input map, so previously-seen activities survive polls whose pages no longer include them
export function mergeActivityStatuses(
    accumulated: Map<string, ActivityStatusResponse>,
    freshItems: ActivityStatusResponse[],
): Map<string, ActivityStatusResponse> {
    const merged = new Map(accumulated);

    freshItems.forEach((item) => {
        const existing = merged.get(item.activityId);
        if (!existing || new Date(item.modifiedDate) >= new Date(existing.modifiedDate)) {
            merged.set(item.activityId, item);
        }
    });

    return merged;
}

// process.env.REACT_APP_ENVIRONMENT doesn't work on deployed instances so we have to get the env from the url
export function getBakerEnv() {
    const url = window.location.hostname;

    if (url.includes('alpha-') || url.includes('beta-') || url.includes('gengar-') || url.includes('staging-')) {
        return 'alpha';
    } else if (url.includes('localhost')) {
        return 'dev';
    } else {
        return 'prod';
    }
}

// Each deploy target injects the Cloud Baker host for the region it serves (BakerApiUrl in deployment/deploy-targets.sh).
// That is the only way to reach a partition with its own domain, such as China. Builds that inject nothing (local
// development) fall back to the global naming convention.
export function getBakerApiBaseUrl(awsRegion: string, env: string) {
    const injectedBaseUrl = process.env.REACT_APP_BAKER_API_URL?.replace(/\/+$/, '');

    return (
        injectedBaseUrl || `https://of-${env}-${getApiRegion(awsRegion)}-cloudbaker-api.openfield.catapultsports.com`
    );
}

function getApiRegion(awsRegion: string) {
    const parts = awsRegion.split('-');
    return parts[0][0] + parts[1][0] + parts[2][0];
}

const BASE_TIME = Date.now();
const INTERVAL_MS = 15 * 60 * 1000; // 15 min apart, so activity[0] is always the most recently modified

export function getMockActivityStatus(activityId: string, listIndex = 0): ActivityStatusResponse {
    return {
        activityId,
        status: generateMockActivityStatus(),
        // listIndex keeps earlier-listed mock activities consistently more recent, matching the sort order on real data
        modifiedDate: new Date(BASE_TIME - listIndex * INTERVAL_MS).toISOString(),
        // no activityName here (same as the real API response) so ActivityStatusElement fetches the real activity name
    };
}

export function getMockAthletesStatus(activityId: string): AthleteStatusResponse[] {
    const athleteStatuses = generateMockAthleteStatuses(activityId);
    return athleteStatuses;
}

type BakedStatus = {
    baked: boolean | undefined;
    live: number | undefined;
    munged: number | undefined;
    total: number | undefined;
};

const parseBakedStatus = (status: string): BakedStatus => {
    const match = status.match(/baked\s+(\d+)-(\d+)-(\d+)/);
    if (!match) {
        return { baked: undefined, live: undefined, munged: undefined, total: undefined };
    }

    return {
        baked: true,
        live: parseInt(match[1], 10),
        munged: parseInt(match[2], 10),
        total: parseInt(match[3], 10), // # of unique athletes added to at least one period in the activity
    };
};

export const getStatusVariant = (
    status: string,
    activityAthletesCount?: number,
): EveryActivityStatusVariant | undefined => {
    const bakedStatusDetails = parseBakedStatus(status);
    const { munged, total } = bakedStatusDetails;
    const totalCount = total ?? activityAthletesCount; // fallback from useGetAthletesInActivity

    // compute athletes not yet munged
    let anyNotMunged = false;
    let notMungedCount = 0;
    if (munged !== undefined && totalCount !== undefined) {
        anyNotMunged = munged < totalCount;
        notMungedCount = totalCount - munged;
    }
    // extra statuses computed in frontend
    if (status.includes('error')) {
        return status.includes('no files synced')
            ? 'noFilesSyncedError'
            : status.includes('no PIP')
            ? 'noPipError'
            : 'error';
    } else if (status.includes('baked')) {
        return anyNotMunged && totalCount !== undefined
            ? `processedWithMissing-${notMungedCount}-${totalCount}`
            : 'processed';
    } else if (isActivityProcessing(status)) {
        return 'processing';
    }

    return undefined;
};

export function getTagVariant(variant: EveryActivityStatusVariant): string {
    // Handle the dynamic pattern first
    if (variant.startsWith('processedWithMissing')) {
        return 'error';
    }

    const statusTagVariants: Record<EveryActivityStatusVariant, string> = {
        processing: 'warning',
        processed: 'success',
        error: 'error',
        noFilesSyncedError: 'error',
        noPipError: 'error',
    };

    return statusTagVariants[variant];
}

type ErrorSource = 'modal' | 'banner';

interface ErrorMessageParams {
    __: Translator;
    variant?: EveryActivityStatusVariant; // use for Modal
    code?: string; // use for Banner
    activityAthletesCount?: number;
    source?: ErrorSource;
}

export function resolveErrorMessage({
    __,
    code,
    variant,
    activityAthletesCount,
    source = 'modal',
}: ErrorMessageParams): string {
    const noFilesSynced = code?.includes('no files synced') || variant === 'noFilesSyncedError';
    const noPip = code?.includes('no PIP') || variant === 'noPipError';
    const hasMissing = !!variant?.startsWith('processedWithMissing-');

    if (noPip) {
        return __('data_processing.no_athletes_in_periods');
    }

    if (source === 'modal') {
        if (noFilesSynced) {
            return `${__('data_processing.athletes_with_missing_raw_files')} (${activityAthletesCount ?? 0})`;
        }
        if (hasMissing) {
            const count = variant?.split('-')[1];
            return `${__('data_processing.athletes_with_missing_raw_files')} ${count ? `(${count})` : ''}`;
        }
    }
    if (source === 'banner') {
        if (noFilesSynced) {
            return __('data_processing.no_raw_data');
        }
    }

    return __('data_processing.processing_error_status');
}

export function resolveErrorGuidance({ __, code, variant }: ErrorMessageParams): string {
    const noFilesSynced = code?.includes('no files synced') || variant === 'noFilesSyncedError';
    const noPip = code?.includes('no PIP') || variant === 'noPipError';
    const hasMissing = !!variant?.startsWith('processedWithMissing-');

    if (noFilesSynced || hasMissing) {
        return __('data_processing.upload_raw_data_guidance');
    }

    if (noPip) {
        return __('data_processing.check_mappings_guidance');
    }

    const baseGuidance = __('data_processing.contact_support_guidance');
    const errorDetail = code ? code.split(':')?.[1]?.trim() : '';

    return errorDetail ? `${baseGuidance} - ${errorDetail}` : baseGuidance;
}

export function getStatusMessage(__: Translator, variant: EveryActivityStatusVariant): string {
    if (variant === 'processing') {
        return __('data_processing.processing_status');
    }

    if (variant === 'processed') {
        return __('data_processing.processed_status');
    }
    // generic error message as fallback case
    return __('data_processing.processing_error_status');
}

export function getStatusGuidance(__: Translator, variant: EveryActivityStatusVariant): string {
    if (variant === 'processing') {
        return __('data_processing.processing_status_guidance');
    }

    if (variant === 'processed') {
        return __('data_processing.processed_status_guidance');
    }
    // generic error guidance as fallback case
    return __('data_processing.processing_error_status_guidance');
}
